import { SquareArrowOutUpRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import SentimentNegativeIcon from '@/assets/icons/SentimentNegativeIcon';
import SentimentNeutralIcon from '@/assets/icons/SentimentNeutralIcon';
import SentimentPositiveIcon from '@/assets/icons/SentimentPositiveIcon';
import { StaticFilterViewer } from '@/features/StructuredFilter/components/StaticFilterViewer';
import type { HeySynclyStudioItem } from '@/features/Studio/model';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import lightModeColor from '@/styles/colors/color';
import { FeedbackSentiment } from '@/types/feedbackConst';

export const StudioItemSourceView = (props: {
  item: HeySynclyStudioItem;
  showLinkedChat?: boolean;
  collapsiblePrompt?: boolean;
}) => {
  const { item, showLinkedChat = true, collapsiblePrompt = false } = props;
  const wr = useWorkspaceRouter();
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  const filterQueryFormats = useMemo(() => {
    return JSON.parse(item.filter_string) as StructuredFilterQueryFormatT[];
  }, [item.filter_string]);

  const isDraft = item.is_draft;
  const totalFeedbackCount =
    item.feedback.negative_count +
    item.feedback.neutral_count +
    item.feedback.positive_count;
  const showLinkedChatLink =
    showLinkedChat &&
    !isDraft &&
    item.reference_chat_id &&
    item.reference_message_id;
  const positiveRatio =
    totalFeedbackCount > 0
      ? Math.round((item.feedback.positive_count / totalFeedbackCount) * 100)
      : 0;
  const negativeRatio =
    totalFeedbackCount > 0
      ? Math.round((item.feedback.negative_count / totalFeedbackCount) * 100)
      : 0;
  const neutralRatio =
    totalFeedbackCount > 0
      ? Math.round((item.feedback.neutral_count / totalFeedbackCount) * 100)
      : 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        {showLinkedChatLink && (
          <div className="flex items-center gap-1.5">
            <span className="text-base font-medium text-foreground">
              Linked Chat
            </span>
            <SquareArrowOutUpRight
              className="size-3.5 text-muted-foreground cursor-pointer"
              onClick={async () => {
                if (!item.reference_chat_id || !item.reference_message_id)
                  return;

                void wr.push(
                  `/hey-syncly?chat_id=${item.reference_chat_id}&message_id=${item.reference_message_id}`,
                );
              }}
            />
          </div>
        )}
        <div className="flex">
          <span className="text-base font-medium text-foreground">
            {totalFeedbackCount} feedbacks
          </span>
          <FeedbackRatio
            positiveRatio={positiveRatio}
            negativeRatio={negativeRatio}
            neutralRatio={neutralRatio}
          />
        </div>
        <StaticFilterViewer formats={filterQueryFormats} />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">
          {isDraft ? 'Chat input' : 'Prompt'}
        </span>
        <p
          className={`whitespace-pre-wrap text-sm text-foreground ${
            collapsiblePrompt && !isPromptExpanded ? 'line-clamp-3' : ''
          }`}
        >
          {item.instruction}
        </p>
        {collapsiblePrompt && item.instruction && (
          <button
            type="button"
            className="w-fit text-xs font-medium text-primary hover:underline"
            onClick={() => setIsPromptExpanded((expanded) => !expanded)}
          >
            {isPromptExpanded ? '접기' : '더보기'}
          </button>
        )}
      </div>
    </div>
  );
};

const ICON_SIZE = 14;

const FeedbackRatio = ({
  positiveRatio = 0,
  negativeRatio = 0,
  neutralRatio = 0,
}: {
  positiveRatio: number;
  negativeRatio: number;
  neutralRatio: number;
}) => {
  return (
    <div className="flex items-center gap-3 ml-2">
      <div className="flex items-center gap-1">
        <SentimentPositiveIcon
          width={ICON_SIZE}
          height={ICON_SIZE}
          fill={lightModeColor.sentiment[FeedbackSentiment.POSITIVE]}
        />
        <span className="text-sm text-muted-foreground">{positiveRatio}%</span>
      </div>
      <div className="flex items-center gap-1">
        <SentimentNegativeIcon
          width={ICON_SIZE}
          height={ICON_SIZE}
          fill={lightModeColor.sentiment[FeedbackSentiment.NEGATIVE]}
        />
        <span className="text-sm text-muted-foreground">{negativeRatio}%</span>
      </div>
      <div className="flex items-center gap-1">
        <SentimentNeutralIcon
          width={ICON_SIZE}
          height={ICON_SIZE}
          fill={lightModeColor.sentiment[FeedbackSentiment.NEUTRAL]}
        />
        <span className="text-sm text-muted-foreground">{neutralRatio}%</span>
      </div>
    </div>
  );
};
